// What a thing is, from the words people and AIs use for it: its kind (what the map filters by and the
// colour behind its icon), the icon on its pin, and how a journey goes. In one place for the web view
// (pins, lists, filters) and Bookings' groups (src/bookgroups.ts), so a thing's kind, its icon and its
// group always agree. Plain functions, used by the server and the web view; which icon each name is
// drawn with is the web view's (web/src/format.ts checks every one is drawn).

// What a thing is, broadly: a stay, a way of getting around, else from its category.
export type Kind = "stays" | "around" | "food" | "sights" | "nature" | "shopping" | "other";

// The AI writes categories in its own words; the pin shows the closest icon, and the same match says
// its kind, so the two always agree. Words match whole ("theatre" isn't food for containing "eat", nor
// "parking" a park), plurals included, and the first row that matches wins: the more specific a row,
// the higher it sits.
export const CATEGORY = [
  // Before food and classes: a cooking class is a class, not a meal.
  ["cooking|cooking class|cooking school|cooking lesson", "pot", "sights"],
  // Before shows: a movie theatre is a cinema.
  ["cinema|movie theat(?:re|er)|movie|film", "video", "sights"],
  // Drinks and sweets before food and cafés: a dessert café is for dessert, a wine bar for wine.
  [
    "dessert|sweet|sweet shop|snack|ice cream|gelato|soft serve|patisserie|pâtisserie|cake|wagashi|mochi|candy|chocolate|cr[eê]pe|donut|doughnut|parfait",
    "icecream",
    "food",
  ],
  ["coffee|caf[eé]|tea|teahouse|tea house|tea room|kissaten|matcha", "coffee", "food"],
  ["distillery|whisk(?:e)?y|sake|shochu|spirit|gin|rum", "bottle", "food"],
  ["wine|winery|vineyard|wine bar|wine tasting", "wine", "food"],
  ["beer|brewery|brewpub|pub|gastropub|taproom|beer hall|beer garden|craft beer", "beer", "food"],
  [
    "food|restaurant|eat|eatery|meal|dinner|lunch|breakfast|brunch|ramen|sushi|izakaya|bakery|street food|food hall|food court|steakhouse|steak|bbq|barbecue|yakiniku|yakitori|tempura|udon|soba|noodle|kaiseki|okonomiyaki|takoyaki|tonkatsu|gyoza|curry|pizza|pizzeria|burger|bistro|brasserie|diner|grill|trattoria|dim sum|dumpling|seafood|tapas|omakase|teppanyaki|deli|canteen|hawker|food stall|yatai",
    "meal",
    "food",
  ],
  // Before bars: a golf club is sport.
  [
    "sport|stadium|arena|sumo|baseball|football|soccer|rugby|tennis|golf|golf club|golf course|basketball|wrestling|match|race|racing",
    "ball",
    "sights",
  ],
  ["bar|cocktail|cocktail bar|lounge|nightlife|night ?club|club|disco|speakeasy", "cocktail", "food"],
  ["karaoke", "mic", "sights"],
  // A stay's own words, for a place filed as one (a stay itself is always the bed).
  [
    "hotel|ryokan|hostel|guest ?house|inn|minshuku|capsule hotel|lodge|airbnb|homestay|motel|accommodation",
    "bed",
    "stays",
  ],
  // Before arcades and streets: a shopping arcade or street is for shopping.
  [
    "market|shop|shopping|mall|store|souvenir|boutique|outlet|department store|shopping arcade|shopping street|shotengai|bookstore|bookshop|flea market|antique",
    "bag",
    "shopping",
  ],
  ["arcade|game cent(?:re|er)|gaming|pachinko|video game", "gamepad", "sights"],
  // Before palaces, shows and towns: a historic or entertainment district is an area.
  ["neighbou?rhood|district|quarter|downtown|chinatown", "district", "sights"],
  ["village|old town|town|hamlet|townscape", "house", "sights"],
  // Before parks: a theme park is not a garden, nor a deer park. A park's own short name and its pass to skip
  // the queues are its tickets ("Decide on USJ Express Pass", on the founder's trip, 2026-10-23).
  [
    "theme park|amusement|amusement park|funfair|fair|ferris wheel|ferris|usj|universal studios|disneyland|disneysea|disney sea|express pass",
    "wheel",
    "sights",
  ],
  ["zoo|safari|wildlife|animal park|deer park", "paw", "sights"],
  ["aquarium", "fish", "sights"],
  ["temple|shrine|church|cathedral|mosque|pagoda|torii|basilica|chapel|synagogue|monastery", "torii", "sights"],
  // Before shows: a light show is an illumination.
  ["illumination|light ?up|lights|light show|lantern", "lantern", "sights"],
  ["festival|matsuri|firework|hanabi|carnival|parade", "fireworks", "sights"],
  [
    "show|performance|theat(?:re|er)|kabuki|noh|bunraku|musical|opera|ballet|cabaret|comedy|circus|entertainment",
    "mask",
    "sights",
  ],
  ["concert|live music|music|live house|jazz|gig|orchestra|recital", "music", "sights"],
  ["geisha|maiko|geiko|kimono", "fan", "sights"],
  ["class|lesson|workshop|craft|pottery|calligraphy|kintsugi|origami|ikebana", "brush", "sights"],
  ["castle|palace|museum|gallery|galleries|historic|historical|heritage|ruin|fort|fortress", "palace", "sights"],
  // After museums: an art museum is a museum.
  ["art|street art|mural|teamlab", "palette", "sights"],
  ["landmark|monument|statue|memorial|obelisk|buddha|daibutsu", "monument", "sights"],
  ["event|exhibition|expo|convention|conference|ceremony", "calendar", "sights"],
  // Before tours and rivers: a boat tour or a river cruise is on a boat.
  ["cruise|boat|boat tour|boat ride|boat trip|sightseeing boat|yakatabune", "boat", "sights"],
  ["tour|guided tour|walking tour|sightseeing tour|bus tour|day tour", "flag", "sights"],
  // Before stations: a ropeway station is for the ropeway.
  ["cable ?car|ropeway|gondola|funicular|chair ?lift|aerial tram", "cablecar", "around"],
  ["ferry|ferry terminal", "ferry", "around"],
  ["bus|bus stop|bus station|bus terminal|coach", "bus", "around"],
  // Before parks: a car park is for the car.
  ["parking|car park|parking lot|garage|car rental|rental car", "car", "around"],
  ["bike|bicycle|cycling|bike rental", "bike", "around"],
  // A rail pass or an airport express by its name is a train ("Buy Hokuriku Arch Pass ×4", "Book HARUKA
  // One-way", on the founder's trip, 2026-10-23).
  [
    "station|train|rail|railway|subway|metro|shinkansen|haruka|skyliner|narita express|jr pass|arch pass|railpass|eurail|interrail",
    "train",
    "around",
  ],
  // A card that pays for trains and buses alike: getting around, not one way of it ("Get T-money cards").
  ["suica|pasmo|icoca|t ?money|ic card|transit card|travel card|octopus card|oyster card", "route", "around"],
  ["airport|flight", "plane", "around"],
  ["port|harbou?r|pier|marina|wharf|dock|jetty|quay", "anchor", "around"],
  // After the ways of getting around above: "transport" alone says only that it's a way there.
  ["transport|transportation|transit|getting around", "route", "around"],
  [
    "tower|view|viewpoint|view point|lookout|observatory|observation deck|observation|skyline|rooftop|scenic spot|overlook|panorama|night view",
    "tower",
    "sights",
  ],
  ["photo|photo ?spot|photogenic", "camera", "sights"],
  // "sight" itself, as AIs file a place to see (2026-10-23: two on a test trip fell to Other).
  ["attraction|sight|sightseeing|must see|things to do|tourist spot|ticket|admission", "ticket", "sights"],
  ["activity|activities|experience|adventure", "sparkle", "sights"],
  ["day trip|excursion|side trip", "map", "sights"],
  ["walk|walking|stroll|promenade|path|walking path|boardwalk", "walk", "sights"],
  ["street|alley|alleyway|lane|road|avenue|boulevard|yokocho|crossing", "street", "sights"],
  ["square|plaza|piazza|fountain", "fountain", "sights"],
  ["bridge", "bridge", "sights"],
  // A hot spring is an onsen, whichever word is used.
  ["onsen|hot ?spring|bath|bathhouse|spa|sauna|sento|public bath|hammam", "onsen", "sights"],
  ["thermal|geyser", "onsen", "nature"],
  ["waterfall|falls|cascade", "waterfall", "nature"],
  ["lake|pond|lagoon|reservoir|wetland|marsh", "lake", "nature"],
  ["river|stream|canal|creek|riverside|riverbank", "wave", "nature"],
  // Before mountains and parks: a ski area is for skiing, a snow park for snow.
  ["ski|skiing|snow|snowboard|ski resort", "ski", "nature"],
  [
    "mountain|mount|mt|hike|hiking|trail|trek|trekking|volcano|peak|summit|hill|gorge|valley|canyon|cave|cliff",
    "mountain",
    "nature",
  ],
  [
    "park|garden|forest|nature|bamboo|grove|botanical|wood|meadow|flower|blossom|cherry blossom|sakura|maple|autumn leaves|koyo",
    "tree",
    "nature",
  ],
  [
    "beach|swim|swimming|pool|island|coast|bay|snorkel|snorkelling|snorkeling|diving|dive|surf|surfing",
    "swim",
    "nature",
  ],
] as const satisfies readonly (readonly [words: string, icon: string, kind: Kind])[];
export type CategoryIcon = (typeof CATEGORY)[number][1];

// Lower case, punctuation as spaces, and a plural read as its singular ("galleries", "activities").
const words = (text: string | undefined) =>
  ` ${(text ?? "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim()
    .replace(/(\p{L}{3,})ies(?= |$)/gu, "$1y")} `;
// A row's words, each whole: between spaces, with an optional plural "s" or "es".
const wholeWords = (alternatives: string) => new RegExp(` (?:${alternatives})(?:e?s)? `, "u");
const MATCHERS = CATEGORY.map(([alternatives, icon, kind]) => ({ pattern: wholeWords(alternatives), icon, kind }));

// The row some words fall in (a category, or a to-do's title), or none.
export const byCategory = (text: string | undefined): { icon: CategoryIcon; kind: Kind } | undefined => {
  const said = words(text);
  return MATCHERS.find(({ pattern }) => pattern.test(said));
};

// Anything with an item's shape: the server's stored records and the web view's items alike.
export interface Categorized {
  type: string;
  category?: string | undefined;
  title?: string | undefined;
  from?: unknown;
}

// Matched the same way as `iconOf` below, so a thing's kind (its filter and its colour) and its icon
// always agree.
export function kindOf(item: Categorized): Kind {
  if (item.type === "stay") return "stays";
  if (item.type === "move") return "around";
  if (item.type === "choice") return item.from ? "around" : "stays";
  return byCategory(item.category)?.kind ?? "other";
}

// How a journey goes, matched the same whole-word way.
export const MODE = [
  ["flight|plane|fly|flying", "plane"],
  ["train|rail|railway|shinkansen|metro|subway|tram|haruka|skyliner", "train"],
  ["bus|coach|shuttle", "bus"],
  ["ferry|boat", "ferry"],
  ["taxi|cab|uber|grab", "taxi"],
  ["car|drive|driving|transfer|limo|limousine|chauffeur|private car|mpv|van", "car"],
  ["bike|bicycle|cycle|cycling", "bike"],
  ["walk|walking|foot", "walk"],
] as const;
export type Mode = (typeof MODE)[number][1];
const MODES = MODE.map(([alternatives, mode]) => ({ pattern: wholeWords(alternatives), mode }));

// How some words say a journey goes ("flight", "Changi → Home (limo)"), or none.
export const modeIn = (text: string | undefined): Mode | undefined => {
  const said = words(text);
  return MODES.find(({ pattern }) => pattern.test(said))?.mode;
};

// How a journey goes: its category says, failing that its title might ("Changi → Home (limo)").
export const modeOf = (item: Categorized): Mode | undefined => modeIn(item.category) ?? modeIn(item.title);

// A thing's icon: a stay's bed, a journey's mode, a goal's star, else its category's.
export function iconOf(item: Categorized): CategoryIcon | Mode | "bed" | "route" | "star" | "pin" {
  if (item.type === "stay") return "bed";
  if (item.type === "choice") return item.from ? "route" : "bed";
  if (item.type === "move") return modeOf(item) ?? "route";
  if (item.type === "goal") return "star";
  return byCategory(item.category)?.icon ?? "pin";
}
